import React, { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { Box, CircularProgress, Divider, Grid } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { useOutletContext } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

import MainCard from "components/cards/MainCard";
import { SolidButton } from "components/buttons/SolidButton";
import { SolidButton as SolidRectangleButton } from "components/buttons/SolidRectangleButton";
import ColumnFilterToggle from "components/Table/ColumnFilterToggle";
import ColumnVisibilityToggle from "components/Table/ColumnVisibilityToggle";
import DrawerField from "components/Drawer";
import DateRangePicker from "components/DateRangePicker";
import SearchInput from "components/SearchInput";
import TableField from "components/Table";

import allEventsColumns from "pages/Events/AllEvents/allEventsColumns";
import { ReactComponent as ExportIcon } from "assets/images/icons/icn_btn_export.svg";
import iconRefresh from "assets/images/icons/icn_refresh.svg";
import { exportTableDataToCSV } from "utils/export";

const VISIBILITY_GROUP_DATA = [
  {
    groupNames: ["Status"],
    columns: {
      Status: [
        "severity", "createdAt", "siteName", "eventType", "deviceType",
        "deviceName", "deviceMacAddress", "description", "clientName", "clientMacAddress",
      ],
    },
  },
];

const normalizeEvent = (event, mspName) => ({
  ...event,
  // Enterprise Events API returns seconds. Keep mock data backwards compatible.
  createdAt: event.createdAt && event.createdAt < 100000000000 ? event.createdAt * 1000 : event.createdAt,
  // MSP Events are scoped to the selected MSP; always show that MSP name.
  belongToMspName: mspName ?? event.belongToMspName,
  payload: event.payload ?? {},
});

const isOrgLevelAcknowledgeable = (event) => event.belongToMspId == null;

const MspEvents = () => {
  const { auth } = useAuthScope();
  const { scopeReady } = useOutletContext();
  const { dataMap, getData, sendRequest, sendBatch } = useDataSourceManager();
  const columnHelper = createColumnHelper();
  const [eventsUuid] = useState(uuidv4());
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [licenseUuid] = useState(uuidv4());
  const [isPro, setIsPro] = useState(false);
  const [licenseLoaded, setLicenseLoaded] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const to = dayjs();
    return { from: dayjs().subtract(7, "day").startOf("day"), to };
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState({});

  const selectedBreadcrumbs = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
    } catch {
      return {};
    }
  }, [scopeReady]);
  const selectedRealm = selectedBreadcrumbs.realm ?? {};
  const entId = selectedRealm.id;
  // The enterprise realm is the source of truth for every MSP Events request.
  const isMspRealm = selectedRealm.hasManageOrg === true;
  const permission = auth.permissions || selectedRealm.roleName;
  const canAcknowledge = permission === "Admin" || permission === "Super Admin";
  const maxRangeDays = isPro ? 30 : 7;
  const maxHistoryDays = isPro ? 180 : 7;
  const deviceDrawerAuth = useMemo(() => ({
    realm: auth.realm,
    permissions: auth.permissions,
    scopeLevel: drawerData.siteId ? "site" : "org",
    orgs: { id: drawerData.orgId },
    sites: { id: drawerData.siteId },
  }), [
    auth.realm,
    auth.permissions,
    drawerData.siteId,
    drawerData.orgId,
  ]);

  const table = useTableInstance({
    tableName: "msp-events",
    data: events,
    getRowId: (row) => row.id,
    columns: allEventsColumns(
      columnHelper,
      canAcknowledge ? permission : null,
      setDrawerOpen,
      setDrawerData,
      true,
      undefined,
      {
        enableSelection: true,
        showAcknowledgeAction: false,
        // MSP-owned logs cannot be acknowledged through the organization-level resource.
        getRowDisabled: (event) => !isOrgLevelAcknowledgeable(event),
      },
    ),
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["belongToMspName", "orgName", "siteName", "deviceName", "deviceMacAddress", "description", "clientName", "clientMacAddress"],
    enableRowSelection: true,
    hiddenColumns: ["clientName", "clientMacAddress"],
  });

  const readEvents = (range = dateRange) => {
    if (!entId) return;
    setLoading(true);

    const now = dayjs();
    const withCurrentTime = (date) => date
      .hour(now.hour())
      .minute(now.minute())
      .second(now.second())
      .millisecond(now.millisecond());

    const from = range?.from ? dayjs(range.from) : null;
    const to = range?.to ? dayjs(range.to) : null;
    const isSingleDay = from && to && from.isSame(to, "day");

    sendRequest(
      "/ents/:entId/events.read",
      eventsUuid,
      { entId },
      from && to
        ? {
            filter: {
              from: isSingleDay ? from.startOf("day").valueOf() : withCurrentTime(from).valueOf(),
              to: to.isSame(now, "day")
                ? now.valueOf()
                : (isSingleDay ? to.endOf("day") : withCurrentTime(to)).valueOf(),
            },
          }
        : {},
    );
  };

  const selectedRows = table.table.getSelectedRowModel().rows;

  async function acknowledgeSelectedEvents() {
    if (!entId || !selectedRows.length) return;

    const eventIdsByOrg = selectedRows.reduce((groups, { original: event }) => {
      if (!isOrgLevelAcknowledgeable(event) || !event.orgId) return groups;
      groups[event.orgId] = [...(groups[event.orgId] ?? []), event.id];
      return groups;
    }, {});

    const requests = Object.entries(eventIdsByOrg).map(([orgId, eventIds]) => ({
      apiKey: "/ents/:entId/orgs/:orgId/events/acknowledge.update",
      requestUuid: uuidv4(),
      restfulPlaceholders: { entId, orgId },
      params: { data: { eventIds } },
    }));
    if (!requests.length) return;

    const { results } = await sendBatch(requests);
    const acknowledgedIds = new Set(
      results.flatMap((result) => (result?.data ?? []))
        .filter((item) => item.status === "success")
        .map((item) => item.eventId),
    );

    if (acknowledgedIds.size) {
      setEvents((current) => current.map((event) => (
        acknowledgedIds.has(event.id) ? { ...event, acknowledge: true } : event
      )));
    }
    table.table.resetRowSelection();
  }

  useEffect(() => {
    if (scopeReady && isMspRealm && entId) {
      sendRequest("/ents/:entId/orgs/license/summary.read", licenseUuid, { entId });
    }
  }, [scopeReady, isMspRealm, entId]);

  useEffect(() => {
    const readResponse = getData(eventsUuid);
    if (readResponse?.data) {
      setEvents(readResponse.data.map((event) => normalizeEvent(event, auth.realm?.name)));
      setLoading(false);
    }

    if (!licenseLoaded) {
      const licenseResponse = getData(licenseUuid);
      if (licenseResponse) {
        const orgs = licenseResponse.data || [];
        const hasPro = orgs.some(org =>
          (org.sites || []).some(site => site?.services?.unityPro === true)
        );
        setIsPro(hasPro);
        setLicenseLoaded(true);
        const days = hasPro ? 30 : 7;
        const newRange = {
          from: dayjs().subtract(days, "day").startOf("day"),
          to: dayjs(),
        };
        setDateRange(newRange);
        readEvents(newRange);
      }
    }
  }, [dataMap, auth.realm?.name]);

  return (
    <>
      <MainCard>
        <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {canAcknowledge && selectedRows.length > 0 && (
              <>
                <SolidRectangleButton onClick={acknowledgeSelectedEvents}>
                  Acknowledge
                </SolidRectangleButton>
                <Divider sx={{ width: "1px", height: "40px", margin: "20px 20px" }} orientation="vertical" flexItem />
              </>
            )}
          </Box>
          <Grid container sx={{ width: "auto", alignItems: "center" }}>
            <Box sx={{ display: "flex", padding: "3px 10px 3px", marginRight: "5px" }}>
              <SearchInput
                placeholder="Search by Name, MAC, Description"
                value={table.globalFilter}
                onChange={table.setGlobalFilter}
              />
            </Box>
            <DateRangePicker
              value={dateRange}
              maxRangeDays={maxRangeDays}
              minDate={dayjs().subtract(maxHistoryDays, "day").startOf("day")}
              defaultValue={{
                from: dayjs().subtract(isPro ? 30 : 7, "day").startOf("day"),
                to: dayjs(),
              }}
              onChange={(range) => {
                setDateRange(range);
                readEvents(range);
              }}
            />
            <ColumnFilterToggle
              {...table}
              filterData={["severity", "belongToMspName", "orgName", "siteName", "eventType", "deviceType"]}
            />
            <ColumnVisibilityToggle
              maxWidth={360}
              {...table}
              groupedData={VISIBILITY_GROUP_DATA}
              hiddenCheck={["severity", "createdAt", "siteName", "eventType", "deviceType", "deviceName"]}
            />
            <Box margin="10px 10px 0px 8px" sx={{ cursor: "pointer" }} onClick={() => readEvents()}>
              <img src={iconRefresh} alt="Refresh events" />
            </Box>
            <SolidButton
              startIcon={<ExportIcon />}
              width="100px"
              height="32px"
              onClick={() => exportTableDataToCSV(table)}
            >
              Export
            </SolidButton>
          </Grid>
        </Grid>
        <TableField
          {...table}
          overlayData={["acknowledge"]}
          pendingChildren={loading && (
            <Box sx={{ display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
          )}
        />
      </MainCard>
      {drawerOpen && (drawerData.key === "DeviceNameWithMACAsLink" || drawerData.key === "PayloadDeviceNameWithMACAsLink") && (
        <DrawerField
          contentKey={`msp-events-device-${drawerData.deviceId ?? drawerData.payload?.deviceId ?? drawerData.deviceMacAddress}`}
          open={drawerOpen}
          width="auto"
          handleClose={() => setDrawerOpen(false)}
          commonContent="devices"
          contentProps={{
            fetchFlag: true,
            auth: deviceDrawerAuth,
            deviceId: drawerData.deviceId ?? drawerData.payload?.deviceId ?? drawerData.deviceMacAddress,
          }}
        />
      )}
      {drawerOpen && (drawerData.key === "AccountNameWithUniqueLink" || drawerData.key === "ChangedAccountNameWithUniqueLink") && (
        <DrawerField
          contentKey={`msp-events-user-${drawerData.accountId ?? drawerData.payload?.changedAccountId}`}
          open={drawerOpen}
          width="550px"
          handleClose={() => setDrawerOpen(false)}
          commonContent="users"
          contentProps={{
            userId: drawerData.accountId ?? drawerData.payload?.changedAccountId,
            closeDrawerCallback: () => setDrawerOpen(false),
          }}
        />
      )}
      {drawerOpen && drawerData.key === "ClientNameWithMACAsLink" && (
        <DrawerField
          contentKey={`msp-events-client-${drawerData.payload?.macAddress}`}
          open={drawerOpen}
          width="550px"
          handleClose={() => setDrawerOpen(false)}
          commonContent="clients"
          contentProps={{
            auth: {
              ...auth,
              scopeLevel: "site",
              orgs: { id: drawerData.orgId },
              sites: { id: drawerData.siteId },
            },
            selectedSiteId: drawerData.siteId,
            selectedClientId: drawerData.payload?.macAddress,
          }}
        />
      )}
    </>
  );
};

export default MspEvents;
